<script lang="ts" setup>
import { ref, watch } from 'vue';
import type { VTextField } from 'vuetify/components';
import { CustomerMgmtTypes, useEditGroup } from '@ilteducation/customer-mgmt';
import Modal from '../../Modal.vue';
import useModal, { ModalTypes } from '../../../composables/useModal';
import { useLocale } from '../../../composables/useLocale';
import useToast from '../../../composables/useToast';
import OrganizationGroupsTable from './OrganizationGroupsTable.vue';
import { computed } from 'vue';
import { useSidebarUser } from '../../../composables/useSidebarUser';
import { useAppEvents } from '../../../events/appEvents';

const { t } = useLocale();

type Props = {
  name: ModalTypes;
  user: CustomerMgmtTypes.GetUserResponse;
  memberships: CustomerMgmtTypes.GetMembershipsResponse;
  organizationUid: string;
};

const props = defineProps<Props>();
const loading = ref<boolean>(false);
const { addMember, removeMember } = useEditGroup();
const { getUserGroups } = useSidebarUser();
const { emitAppEvent } = useAppEvents();
const { close } = useModal(props.name);
const { toast } = useToast();

const userMemberships = ref<string[]>(props.memberships.data.map((membership) => membership.group.uid));
watch(
  () => props.memberships,
  () => {
    userMemberships.value = props.memberships.data.map((membership) => membership.group.uid);
  },
);

const changes = computed(() => {
  const currentMembershipsGroupUids = props.memberships.data.map((membership) => membership.group.uid);
  const add = userMemberships.value.filter((groupUid) => !currentMembershipsGroupUids.includes(groupUid));
  const remove = currentMembershipsGroupUids.filter((groupUid) => !userMemberships.value.includes(groupUid));

  return { add, remove };
});

const onSave = async () => {
  loading.value = true;
  const { add, remove } = changes.value;
  const [addResponses, removeResponses] = await Promise.all([
    Promise.allSettled(add.map((groupUid) => addMember(groupUid, props.user.uid))),
    Promise.allSettled(remove.map((groupUid) => removeMember(groupUid, props.user.uid))),
  ]);

  const failedAddResponses = addResponses.filter((resonse) => resonse.status === 'rejected');
  const failedRemoveResponses = removeResponses.filter((resonse) => resonse.status === 'rejected');

  const numberOfErrors = failedAddResponses.length + failedRemoveResponses.length;

  if (numberOfErrors === 0) {
    toast(t('lcp.user_groups_successfully_saved'), 'success');
  } else {
    if (numberOfErrors === addResponses.length + removeResponses.length) {
      toast(t('lcp.user_groups_save_failed'), 'error');
    } else {
      toast(
        t('lcp.user_groups_save_some_failed', {
          addedCount: failedAddResponses.length,
          removedCount: failedAddResponses.length,
        }),
        'warning',
      );
    }
  }

  await getUserGroups(props.user.uid, props.organizationUid, true);
  emitAppEvent({ type: 'membership:updated', organizationUid: props.organizationUid });
  loading.value = false;
  close();
  reset();
};

watch(
  () => [props.user, props.organizationUid],
  () => {
    reset();
  },
);

const hasChange = computed(() => {
  const { add, remove } = changes.value;
  return add.length + remove.length > 0;
});
const actionLabel = computed(() => {
  return `${t('lcp.save')} (${userMemberships.value.length})`;
});

const searchRef = ref<VTextField>();
const search = ref<string>();
const searchString = ref<string>();

const searchChanged = () => {
  searchString.value = searchRef.value?.value ?? undefined;
  search.value = searchString.value;
};

const clearSearch = () => {
  searchString.value = undefined;
  search.value = searchString.value;
};

const reset = () => {
  clearSearch();
};
</script>
<template>
  <Modal
    :loading="loading"
    background-color="surface-container"
    :name="name"
    :title="t('lcp.manage_user_groups_for', { username: user.name })"
    :action-label="actionLabel"
    :action="onSave"
    :action-disabled="!hasChange"
    :show-cancel-action="true"
    :clearable="true"
    width="970"
    :persistent-clear="true"
    :cancel-action-label="t('lcp.cancel')"
    secondary-action-variant="tonal"
  >
    <v-row>
      <v-col :cols="true">
        <v-text-field
          ref="searchRef"
          v-model="search"
          append-inner-icon="mdi-magnify"
          density="comfortable"
          hide-details
          :clearable="true"
          :persistent-clear="true"
          @click:clear="clearSearch"
          @keydown.enter="searchChanged"
          @click:append-inner="searchChanged"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col
        ><OrganizationGroupsTable
          :organization-uid="organizationUid"
          v-model="userMemberships"
          :search="searchString"
          :user="user"
          :memberships="memberships.data"
      /></v-col>
    </v-row>
  </Modal>
</template>
